// ─────────────────────────────────────────────────────────────────────────────
// utils/listTruncation.ts — eine volle Seite sagt es (T-M8.19a; seit T-M8.19b
// die Regel hinter dem Nachlade-Bedienelement)
//
// ⚠ BERICHTIGT AM 2026-08-17 (T-M8.19b, REQ-OPS-036 Praez. 6 — berichtigt statt
// geloescht). WORTLAUT BIS DAHIN:
//   „REQ-OPS-032: die Druckerliste (und gleichgeartete Geraete-/
//    Mandantenlisten) bleibt bei einer festen Obergrenze — echte Pagination ist
//    hier ausdruecklich NICHT gefordert (anders als bei der Auftragsliste,
//    REQ-OPS-033). Wird die Grenze dennoch erreicht, ist das in der Oberflaeche
//    SICHTBAR; eine stille Kuerzung ist unzulaessig."
// Das traf bis zum Entscheid des Auftraggebers vom 2026-08-17 zu und trifft
// seither NICHT mehr zu: REQ-OPS-032 ist neu gefasst — die 100 sind die
// SEITENgroesse, geblaettert wird sehr wohl, und die erreichbare Menge ist
// nicht begrenzt. Der frueher hier gefuehrte „Teilumfang B" (Hoehe der Grenze,
// OP-68) ist damit entfallen.
//
// DIE REGEL SELBST IST UNVERAENDERT GUELTIG UND WIRD WEITER GEBRAUCHT — nur
// ihre FOLGE hat gewechselt: „so viele geladen, wie die Abfrage hoechstens
// liefern darf" hiess frueher „hier ist Schluss" und heisst seit dem
// 2026-08-17 „hier geht es weiter" (`canLoadMoreNamed` in
// `utils/listPagination.ts` ruft genau diese Funktion). Die Auswahl an der
// Seitengrenze ist deterministisch sortiert (REQ-DM-050/050a: Sortierung nach
// `name` vor der Begrenzung — die Abfragen selbst stehen in den Stores).
//
// Warum die Regel als eigene, reine Funktion liegt — und warum sie die Grenze
// als ARGUMENT nimmt statt sie selbst zu kennen:
//
//   • Die MECHANIK (wird die Kuerzung sichtbar?) und die HOEHE der Grenze sind
//     zwei verschiedene Aussagen. Die Hoehe haengt an einer Zahl, die der
//     Auftraggeber noch nicht geliefert hat (Standorte je Mandant, OP-68) — die
//     Mechanik haengt an keiner Zahl: der Hinweis erscheint, sobald die geladene
//     Menge die GELTENDE Grenze erreicht, welche das auch immer ist.
//   • Damit ist die Regel pruefbar, ohne die echte Grenze zu treffen: der Test
//     setzt sie herab, statt hundert Dokumente anzulegen.
//   • Und wenn die Zahl vorliegt, ist Teilumfang B genau eine Konstante in
//     `@/firebase/constants` — an dieser Datei aendert sich dann nichts.
//   *(Die drei Punkte oben sind der Stand bis zum 2026-08-17. Teilumfang B ist
//   entfallen: die Zahl wird nicht mehr gebraucht, weil die Grenze nicht mehr
//   die Liste begrenzt, sondern die SEITE. Die Argumentation bleibt in ihrem
//   Kern richtig — die Regel kennt die Zahl weiterhin nicht, und genau deshalb
//   traegt sie jetzt auch die Seitengroesse.)*
//
// Geschwister mit derselben Aussage: `isScreenLimitReached` in
// `utils/screenChannelJobs.ts` (T-M9.9) fuer die Bildschirm-Abfrage. Dort ist
// die Grenze fest an eine Sicht gebunden und deshalb eingebaut; hier gilt
// dieselbe Regel fuer MEHRERE Listen mit VERSCHIEDENEN Grenzen.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ist die geladene Liste moeglicherweise gekuerzt worden?
 *
 * Wahr, sobald so viele Dokumente geladen wurden, wie die Abfrage hoechstens
 * liefern darf. Mehr laesst sich clientseitig nicht wissen: eine Abfrage mit
 * `limit(n)`, die genau `n` Dokumente liefert, ist von einer gekuerzten nicht
 * zu unterscheiden — die Datenbank sagt nicht, ob dahinter noch etwas lag.
 *
 * Die Unschaerfe ist bewusst zugunsten der Sichtbarkeit aufgeloest: bei exakt
 * `n` vorhandenen Dokumenten meldet die Regel einmal zu viel „gekuerzt". Der
 * umgekehrte Fehler waere der schwerere — eine verschwiegene Kuerzung ist
 * genau der Schaden, gegen den REQ-OPS-032 gebaut ist (dieselbe Abwaegung wie
 * bei `canLoadMore` in `utils/jobPagination.ts`).
 *
 * @param loadedCount Anzahl tatsaechlich geladener Dokumente (seit T-M8.19b:
 *                    Dokumente der zuletzt GELADENEN SEITE).
 * @param listLimit   Die fuer diese Abfrage geltende Grenze (`limit(...)`) —
 *                    seit T-M8.19b die SEITENgroesse.
 */
export function isListTruncated(loadedCount: number, listLimit: number): boolean {
  if (!Number.isFinite(loadedCount) || !Number.isFinite(listLimit)) return false;
  // Eine Grenze von null oder weniger ist keine Grenze, sondern ein Fehler in
  // der Verdrahtung. Sie darf keinen Hinweis ausloesen: „gekuerzt" waere dann
  // eine Behauptung ueber eine Abfrage, die es so gar nicht gibt.
  if (listLimit <= 0) return false;
  return loadedCount >= listLimit;
}
